import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View } from '@/styles/reactnative';
import { AuthCheckBtns } from '@app/components';
import { useToken } from '@app/hooks';
import useTradeStore from '@app/hooks/trade/tradeStore';
import ContractDetails from './ContractDetails';
import DerivativesAccount from './DerivativesAccount';
import DerivativesCheckBoxs from './DerivativesCheckBoxs';
import DerivativesFormBtns from './DerivativesFormBtns';
import DerivativesTpSl from './DerivativesTpsl';
import OrderPriceInput from './OrderPriceInput';
import OrderValueInput from './OrderValueInput';
import PercentageForm from './PercentageForm';
import QuantityCostForm from './QuantityCostForm';

import { DerivativesBodyWrapper } from './styles';
import BigNumber from 'bignumber.js';
import { useBalance } from '@app/hooks/user/mybalance.tsx';
import { useTradeSetting } from '@app/hooks/trade/tradeSetting.tsx';
import { useIndexPrice } from '@app/hooks/trade/indexprice.tsx';

const DerivativesBody = ({ isMobile = false, tab }: any) => {
  const { data: token } = useToken();
  const { data: tradeStore, mutate: mutateTradeStore } = useTradeStore();

  const ep_id = tradeStore?.ep_id;
  const { data: tradeSetting } = useTradeSetting(ep_id);

  const { getPrice } = useIndexPrice(ep_id);

  const [marketPrice, setMarketPrice] = useState<number | undefined>(undefined);
  const isManualInputRef = useRef(false);

  // getPrice 함수를 메모이제이션
  const memoizedGetPrice = useCallback(() => {
    if (getPrice) {
      setMarketPrice(getPrice());
    }
  }, [getPrice]);

  useEffect(() => {
    if (ep_id) {
      memoizedGetPrice();
    }
  }, [ep_id, memoizedGetPrice]);

  const [percentage, setPercentage] = useState('');

  const currency = tradeStore?.pairs?.split('/')[1];
  const { data: balance } = useBalance(currency, 1);
  let availableBalance = new BigNumber(balance?.balance || 0)
    .minus(balance?.lockbalance || 0)
    // .plus(pnl) // TODO: pnl 값 추가
    .toNumber();
  availableBalance = Math.max(availableBalance, 0);

  const orderPrice = tradeStore?.order_price || 0;
  const orderAmount = tradeStore?.order_amount ?? 0;
  const leverage = tradeSetting?.leverage ?? 1;
  const orderType = tradeStore?.main_sub || 'M';

  // console.log("derivative body", {
  //   orderPrice,
  //   orderAmount,
  //   leverage,
  //   availableBalance,
  //   currency,
  //   tradeStore,
  //   tradeSetting,
  // });

  useEffect(() => {
    setPercentage('0');
  }, [tab]);

  // 주문 완료 후 order_amount가 0이 되면 percentage도 초기화
  useEffect(() => {
    if (tradeStore?.order_amount === 0) {
      setPercentage('0');
    }
  }, [tradeStore?.order_amount]);

  const safeToNumber = (value) => {
    const number = new BigNumber(value).toNumber();
    return isNaN(number) ? 0 : number;
  };

  const calculatePercentage = useCallback(
    (price, amount, leverage, availableBalance) => {
      // console.log('getCalculatedPercentage', price, amount, leverage, availableBalance, orderType)

      const cost = new BigNumber(price)
        .multipliedBy(new BigNumber(amount))
        .div(leverage);

      if (!availableBalance || new BigNumber(availableBalance).isZero()) {
        return '0';
      }

      const percentage = cost
        .div(new BigNumber(availableBalance))
        .multipliedBy(100);

      return safeToNumber(percentage).toString();
    },
    []
  );

  const prevOrderAmount = useRef<number | null>(null);
  const prevOrderPrice = useRef<number | null>(null);
  const isUserInput = useRef<boolean>(false);

  // 시장가에서 가격 변화로 인한 자동 수량 업데이트 감지
  useEffect(() => {
    // 시장가에서 수량 변경이 PercentageForm의 가격 변화에 의한 것인지 확인
    if (
      tradeStore?.limit_market === 'M' &&
      prevOrderAmount.current !== null &&
      prevOrderAmount.current !== orderAmount &&
      !isManualInputRef.current
    ) {
      // 이는 자동 수량 업데이트로 간주 (가격 변화로 인한)
      isUserInput.current = false;
    } else if (
      prevOrderAmount.current !== null &&
      prevOrderAmount.current !== orderAmount
    ) {
      // 지정가에서의 수량 변경은 사용자 입력으로 간주
      isUserInput.current = true;
      setTimeout(() => {
        isUserInput.current = false;
      }, 300);
    }

    if (
      prevOrderPrice.current !== null &&
      prevOrderPrice.current !== orderPrice
    ) {
      isUserInput.current = true;
      setTimeout(() => {
        isUserInput.current = false;
      }, 300);
    }

    prevOrderAmount.current = orderAmount;
    prevOrderPrice.current = orderPrice;
  }, [orderAmount, orderPrice, tradeStore?.limit_market]);

  useEffect(() => {
    if (!tradeStore || !tradeStore.limit_market) {
      mutateTradeStore({ ...tradeStore, limit_market: 'L' });
    }

    // 슬라이더에서 직접 조작 중인 경우에만 percentage 재계산 방지
    if (isManualInputRef.current) return;

    // 시장가에서 가격 변화로 인한 수량 변경 시에만 percentage 재계산 방지 (퍼센트 유지)
    if (
      tradeStore.limit_market === 'M' &&
      percentage !== '0' &&
      isUserInput.current
    ) {
      return;
    }

    let calculatedPercentage = '';

    const getCalculatedPercentage = (price, amount) => {
      return calculatePercentage(price, amount, leverage, availableBalance);
    };

    switch (tradeStore.limit_market) {
      case 'L':
      case 'CL':
        calculatedPercentage =
          orderType === 'M'
            ? getCalculatedPercentage(orderPrice, orderAmount)
            : getCalculatedPercentage(
                orderPrice || 0,
                new BigNumber(orderAmount).div(orderPrice)
              );
        break;

      case 'M':
      case 'CM':
        calculatedPercentage =
          orderType === 'M'
            ? getCalculatedPercentage(marketPrice, new BigNumber(orderAmount))
            : getCalculatedPercentage(
                marketPrice || 0,
                new BigNumber(orderAmount).div(marketPrice ?? 1)
              );
        break;

      default:
        calculatedPercentage = '0';
    }

    // console.log("calculatedPercentage", calculatedPercentage);

    // setPercentage(calculatedPercentage);
  }, [
    leverage,
    availableBalance,
    orderType,
    tradeStore?.limit_market,
    calculatePercentage,
    marketPrice,
    orderPrice,
    orderAmount,
  ]);

  return (
    <DerivativesBodyWrapper
      style={isMobile ? { width: '100%', height: 'auto' } : {}}
    >
      {tradeStore?.limit_market === 'L' ||
      tradeStore?.limit_market == 'T' ||
      tradeStore?.limit_market == 'CL' ||
      tradeStore?.limit_market == 'CM' ? (
        <OrderPriceInput
          limit_market={tradeStore?.limit_market}
          isManualInputRef={isManualInputRef}
          isMobile={isMobile}
        />
      ) : null}
      <OrderValueInput
        isManualInputRef={isManualInputRef}
        percentage={percentage}
        setPercentage={setPercentage}
        isMobile={isMobile}
      />
      {/* {tradeStore?.limit_market} */}
      <PercentageForm
        percentage={percentage}
        setPercentage={setPercentage}
        isManualInputRef={isManualInputRef}
      />
      <QuantityCostForm />
      {token ? (
        <View style={{ alignItems: 'flex-start' }}>
          <DerivativesTpSl />
          <DerivativesFormBtns isMobile={isMobile} />
          {/* <DerivativesCheckBoxs /> */}
          <DerivativesAccount isMobile={isMobile} />
        </View>
      ) : (
        <AuthCheckBtns />
      )}

      {/* {!isMobile && <ContractDetails />} */}
    </DerivativesBodyWrapper>
  );
};

export default DerivativesBody;
